import Image from 'next/image'
import { useRouter } from 'next/router'
import { useState } from 'react'
import { toast } from 'react-toastify'

import {
  PaymentMethod,
  SubscriptionStatus,
  SubscriptionType,
  UserPaymentType,
} from '@/gql/generated/graphql'
import Link from '@/update-monitor/Link'
import { Maybe } from '@/utils/common-types'
import { formatDateJP } from '@/utils/date-helpers'
import { formatNumberWithCommas } from '@/utils/number-helpers'
import { cn } from '@/utils/tw-helpers'
import { FragmentOf, graphql, readFragment } from 'gql.tada'
import { useMutation } from 'urql'
import { DeletePlayerConfirmModal, InformSubscriptionModal } from '../modal'
import { Button } from '../ui/button'
import {
  WebPanel,
  WebPanelItem,
  WebPanelItemAction,
  WebPanelItemContent,
  WebPanelItemLabel,
} from '../web-panel'
import { AddPlayerOnMobileModal } from './PlanSettings/AddPlayerOnMobileModal'
import { CancelMainPlanModal } from './PlanSettings/CancelMainPlanModal'
import { CancelPlayerOnMobilePlayerModal } from './PlanSettings/CancelPlayerOnMobilePlayerModal'
import { ResubscribePlayerOnMobileModal } from './PlanSettings/ResubscribePlayerOnMobileModal'

import IconPlus from '@/assets/icons/ic_logic-g-app_plus.svg'
import IconDelete from '@/assets/icons/ic_tart_delete.svg'

interface PlanInfoProps {
  viewer: null | undefined | FragmentOf<typeof PlanInfo_viewer>
}

export function PlanSettings(props: PlanInfoProps) {
  const viewer = readFragment(PlanInfo_viewer, props.viewer)

  const hasNotCancelSiblingPlan = viewer?.players.some(
    (player) =>
      player.subscriptionInfo?.isSiblingDiscounted &&
      !(
        [
          SubscriptionStatus.Canceled,
          SubscriptionStatus.Canceling,
        ] as SubscriptionStatus[]
      ).includes(player.subscriptionInfo?.status as SubscriptionStatus)
  )
  const mainPlanEnable = viewer?.players.some(
    (player) =>
      !player.subscriptionInfo?.isSiblingDiscounted &&
      (
        [
          SubscriptionStatus.Active,
          SubscriptionStatus.Trialing,
        ] as SubscriptionStatus[]
      ).includes(player.subscriptionInfo?.status as SubscriptionStatus)
  )

  return (
    <section className="mx-auto mt-10 max-w-[940px]">
      {viewer?.players.length ? (
        <h3 className="mb-2.5 text-base font-bold md:mb-3.5 md:text-[17px] lg:mb-5 lg:text-xl">
          プレイヤー一覧
        </h3>
      ) : null}

      <div className="relative grid gap-[20px] md:gap-[34px] lg:gap-[40px]">
        {viewer?.players.map((player, index) => (
          <PlayerSubscriptionInfo
            key={player.id}
            index={index}
            paymentType={viewer.paymentType}
            player={player}
            players={viewer.players}
            hasNotCancelSiblingPlan={hasNotCancelSiblingPlan}
            mainPlanEnable={mainPlanEnable}
            defaultPaymentMethod={viewer?.defaultPaymentMethod}
            playersCount={viewer?.players.length || 0}
            hasSubscription={viewer?.hasSubscription}
            hasCurriculumLicense={viewer?.hasCurriculumLicense}
            mainSubscriptionIsTrialing={viewer.mainSubscriptionIsTrialing}
          />
        ))}

        <SiblingDiscountInfo
          paymentType={viewer?.paymentType}
          playersCount={viewer?.players.length || 0}
          defaultPaymentMethod={viewer?.defaultPaymentMethod}
          hasSubscription={viewer?.hasSubscription}
          hasCurriculumLicense={viewer?.hasCurriculumLicense}
        />
      </div>
    </section>
  )
}

export const PlayerSubscriptionInfo_player = graphql(`
  fragment PlayerSubscriptionInfo_player on Player {
    id
    nickname
    name
    subscriptionInfo {
      id
      provider
      status
      nextPaymentDate
      currentPeriodStart
      currentPeriodEnd
      type
      isSiblingDiscounted
      canCancel
      subscriptionEndDate
      nextPaymentAmount
    }
    canDelete
  }
`)

interface PlayerSubscriptionInfoProps {
  index: number
  paymentType: UserPaymentType | null | undefined
  players:
  | null
  | undefined
  | Array<{ id: string; nickname?: string | null | undefined }>
  player: FragmentOf<typeof PlayerSubscriptionInfo_player>
  hasNotCancelSiblingPlan?: boolean
  mainPlanEnable?: boolean
  defaultPaymentMethod?: Maybe<PaymentMethod>
  playersCount: number
  hasSubscription: Maybe<boolean>
  hasCurriculumLicense: Maybe<boolean>
  mainSubscriptionIsTrialing: Maybe<boolean>
}

function PlayerSubscriptionInfo(props: PlayerSubscriptionInfoProps) {
  const player = readFragment(PlayerSubscriptionInfo_player, props.player)
  const subscriptionInfo = player.subscriptionInfo

  const isMainSubscription = !subscriptionInfo?.isSiblingDiscounted
  const paymentTypeIsMobile =
    props.paymentType == UserPaymentType.AppleInApp ||
    props.paymentType == UserPaymentType.GoogleInApp

  const [modal, setModal] = useState<
    | null
    | 'cancel'
    | 'resubscribe'
    | 'cancelMainPlan'
    | 'prohibitCancel'
    | 'playerDelete'
    | 'informSubscription'
  >(null)
  const router = useRouter()

  const [deletePlayerId, setDeletePlayerId] = useState<string>('')

  const [_, deletePlayer] = useMutation(PSI_guardianDeletePlayerMutation)

  const handleDeletePlayer = async () => {
    const { data, error } = await deletePlayer({
      input: { id: deletePlayerId },
    })

    setDeletePlayerId('')
    setModal(null)

    if (!data || error || data.playerDelete?.errors?.length) {
      return toast.error('プレイヤーの削除に失敗しました', {
        toastId: 'player-delete-failed',
        position: 'bottom-right',
      })
    }

    return toast.success('プレイヤーを削除しました', {
      toastId: 'player-delete-successfully',
      position: 'bottom-right',
    })
  }

  return (
    <>
      <section>
        <div className="mb-3 flex justify-between">
          <h4 className="text-xs font-bold text-secondary-text md:text-[13px] lg:text-[15px]">
            プレイヤー{props.index + 1}: {player.nickname ?? '未設定'}
          </h4>
          <button
            type="button"
            onClick={() => {
              setDeletePlayerId(player.id)
              setModal('playerDelete')
            }}
            className="flex items-center text-xs font-bold text-primary-100 disabled:text-outline md:text-[14px] disabled:[&_svg]:text-outline"
            disabled={!player.canDelete}
          >
            <IconDelete className="w-[20px] md:w-[24px]" />
            プレイヤーを削除
          </button>
        </div>

        <WebPanel>
          <WebPanelItem className={cn('py-2.5')}>
            <WebPanelItemLabel>利用プラン</WebPanelItemLabel>

            <WebPanelItemContent className="flex flex-wrap items-center gap-x-[8px] gap-y-[1px]">
              {!!subscriptionInfo?.status &&
                subscriptionInfo.status != SubscriptionStatus.Canceled && (
                  <>
                    {isMainSubscription ? (
                      subscriptionInfo.type !=
                      SubscriptionType.SummerCampaign && (
                        <span className="inline-block h-[15px] bg-primary-100 px-[2px] text-[9px] text-white md:h-[22px] md:px-[8px] md:text-[13px] lg:h-[25px] lg:px-[12px] lg:text-[15px]">
                          主契約
                        </span>
                      )
                    ) : (
                      <span className="inline-block h-[15px] bg-attention px-[2px] text-[9px] text-white md:h-[22px] md:px-[8px] md:text-[13px] lg:h-[25px] lg:px-[12px] lg:text-[15px]">
                        きょうだい割引
                      </span>
                    )}
                  </>
                )}
              <PlanName
                type={subscriptionInfo?.type}
                currentPeriodStart={subscriptionInfo?.currentPeriodStart}
                currentPeriodEnd={subscriptionInfo?.currentPeriodEnd}
                status={subscriptionInfo?.status}
                subscriptionEndDate={subscriptionInfo?.subscriptionEndDate}
              />
            </WebPanelItemContent>

            <WebPanelItemAction
              className={cn(isMainSubscription && 'flex md:w-auto')}
            >
              {subscriptionInfo?.id != null &&
                subscriptionInfo.status != SubscriptionStatus.Canceling &&
                subscriptionInfo.status != SubscriptionStatus.Canceled &&
                (paymentTypeIsMobile ? (
                  <>
                    <Button
                      variant="outline"
                      size="sm"
                      className="ml-2 h-[38px] w-[97px] rounded-xl p-[0px] text-[12px] md:h-[44px] md:w-[134px] md:text-[15px] lg:w-[134px]"
                      onClick={() => setModal('cancel')}
                    >
                      解約
                    </Button>
                    <CancelPlayerOnMobilePlayerModal
                      isOpen={modal == 'cancel'}
                      onRequestClose={() => setModal(null)}
                    />
                  </>
                ) : (
                  <>
                    <Button
                      variant="outline"
                      size="sm"
                      className="ml-2 h-[38px] w-[97px] rounded-xl p-[0px] text-[12px] md:h-[44px] md:w-[134px] md:text-[15px] lg:w-[134px]"
                      onClick={() => {
                        if (
                          props.hasNotCancelSiblingPlan &&
                          isMainSubscription
                        ) {
                          setModal('cancelMainPlan')
                          return
                        }

                        localStorage.removeItem(`survey-responses-${player.id}`)

                        router.push({
                          pathname: '/web/cancel-questionnaire',
                          query: {
                            subscriptionId: subscriptionInfo.id,
                            playerId: player.id,
                          },
                        })
                      }}
                    >
                      解約
                    </Button>
                    <CancelMainPlanModal
                      isOpen={modal == 'cancelMainPlan'}
                      player={props.player}
                      index={props.index}
                      onRequestClose={() => setModal(null)}
                    />
                  </>
                ))}

              {subscriptionInfo?.status == SubscriptionStatus.Canceling &&
                (paymentTypeIsMobile ? (
                  <>
                    <Button
                      variant="outline"
                      size="sm"
                      className="ml-2 h-[38px] w-[52px] rounded-xl text-xs md:h-[44px] md:w-[74px] md:text-[15px] lg:w-[80px]"
                      onClick={() => setModal('resubscribe')}
                    >
                      再開
                    </Button>
                    <ResubscribePlayerOnMobileModal
                      isOpen={modal == 'resubscribe'}
                      onRequestClose={() => setModal(null)}
                    />
                  </>
                ) : (
                  <div className="max-w-[100px] pl-2 md:max-w-[150px]">
                    <span className="text-xs text-secondary-text md:text-sm">
                      プランの再開は、ご利用期間後に承ります
                    </span>
                  </div>
                ))}
              {subscriptionInfo?.status == SubscriptionStatus.Canceled &&
                (paymentTypeIsMobile ? (
                  <>
                    <Button
                      variant="outline"
                      size="sm"
                      className="ml-2 h-[38px] w-[52px] rounded-xl text-xs md:h-[44px] md:w-[74px] md:text-[15px] lg:w-[80px]"
                      onClick={() => setModal('resubscribe')}
                    >
                      再開
                    </Button>
                    <ResubscribePlayerOnMobileModal
                      isOpen={modal == 'resubscribe'}
                      onRequestClose={() => setModal(null)}
                    />
                  </>
                ) : (
                  <>
                    {props.mainSubscriptionIsTrialing ? (
                      <Button
                        variant="outline"
                        size="sm"
                        className="ml-2 h-[38px] w-[97px] rounded-xl p-[0px] text-[12px] md:h-[44px] md:w-[134px] md:text-[15px] lg:w-[134px]"
                        onClick={() => setModal('informSubscription')}
                      >
                        再開
                      </Button>
                    ) : (
                      <Button
                        asChild
                        variant="outline"
                        size="sm"
                        className="ml-2 h-[38px] w-[52px] rounded-xl text-xs md:h-[44px] md:w-[74px] md:text-[15px] lg:w-[80px]"
                      >
                        <Link
                          href={{
                            pathname: '/web/subscription',
                            query: { playerId: player.id },
                          }}
                        >
                          再開
                        </Link>
                      </Button>
                    )}
                  </>
                ))}

              {!subscriptionInfo && (
                <>
                  {props.defaultPaymentMethod ? (
                    <Button
                      variant="outline"
                      size="sm"
                      className="ml-2 h-[38px] w-[97px] rounded-xl p-[0px] text-[12px] md:h-[44px] md:w-[134px] md:text-[15px] lg:w-[134px]"
                      disabled={props.playersCount > 4}
                      onClick={() =>
                        router.push({
                          pathname: '/web/subscription',
                          query: { playerId: player.id },
                        })
                      }
                    >
                      開始
                    </Button>
                  ) : (
                    <Button
                      variant="outline"
                      size="sm"
                      className="ml-2 h-[38px] w-[97px] rounded-xl p-[0px] text-[12px] md:h-[44px] md:w-[134px] md:text-[15px] lg:w-[134px]"
                      onClick={() => setModal('informSubscription')}
                    >
                      開始
                    </Button>
                  )}
                </>
              )}
            </WebPanelItemAction>
          </WebPanelItem>
          <WebPanelItem>
            <WebPanelItemLabel>次回のお支払い</WebPanelItemLabel>

            <WebPanelItemContent>
              <NextPaymentDate
                nextPaymentDate={subscriptionInfo?.nextPaymentDate}
                nextPaymentAmount={subscriptionInfo?.nextPaymentAmount}
                status={subscriptionInfo?.status}
              />
            </WebPanelItemContent>
          </WebPanelItem>
        </WebPanel>
      </section>

      <DeletePlayerConfirmModal
        isOpen={modal === 'playerDelete'}
        onOk={handleDeletePlayer}
        onRequestClose={() => {
          setDeletePlayerId('')
          setModal(null)
        }}
      />

      <InformSubscriptionModal
        isOpen={modal === 'informSubscription'}
        onRequestClose={() => setModal(null)}
        isTrial={props.mainSubscriptionIsTrialing ?? false}
      />
    </>
  )
}

export const PSI_guardianDeletePlayerMutation = graphql(`
  mutation PSI_guardianDeletePlayerMutation($input: PlayerDeleteInput!) {
    playerDelete(input: $input) {
      player {
        id
      }
      errors {
        ... on BaseError {
          __typename
          field
        }
      }
    }
  }
`)

function PlanName(props: {
  type: SubscriptionType | null | undefined
  status: SubscriptionStatus | null | undefined
  currentPeriodEnd: string | null | undefined
  currentPeriodStart: string | null | undefined
  subscriptionEndDate: string | null | undefined
}) {
  const {
    type,
    status,
    currentPeriodStart,
    currentPeriodEnd,
    subscriptionEndDate,
  } = props
  let planName: React.ReactNode
  switch (status) {
    case null:
    case undefined:
    case SubscriptionStatus.Canceled:
      planName = (
        <span className="text-[12px] md:text-[14px] lg:text-[15px]">なし</span>
      )
      break
    default: {
      switch (type) {
        case SubscriptionType.Monthly:
          planName = (
            <span className="text-[12px] text-attention md:text-[14px] lg:text-[15px]">
              月払いプラン
            </span>
          )
          break
        case SubscriptionType.Yearly:
          planName = (
            <span className="text-[12px] text-attention md:text-[14px] lg:text-[15px]">
              年払いプラン
            </span>
          )
          break
        case SubscriptionType.SummerCampaign:
          planName = (
            <span className="text-[12px] text-attention md:text-[14px] lg:text-[15px]">
              夏のキャンペーンプラン
            </span>
          )
          break
      }
    }
  }

  function invoiceRetryDueDate() {
    if (!currentPeriodStart) return null
    const date = new Date(currentPeriodStart)
    date.setDate(date.getDate() + 7)
    return formatDateJP(date)
  }

  return (
    <div>
      <p className="font-bold">{planName}</p>
      {subscriptionEndDate && status == SubscriptionStatus.Canceling && (
        <small className="text-xs">
          ※{' '}
          <span className="text-caution">
            {formatDateJP(subscriptionEndDate)}まで
          </span>
          ご利用可能です
        </small>
      )}
      {status == SubscriptionStatus.PastDue && (
        <p className="text-xs text-caution">
          <small>支払いに失敗しました。支払い方法をご確認ください。</small>
          <br />
          <small>
            ※ {invoiceRetryDueDate()}
            までに支払いが完了しない場合、プランは自動でキャンセルされます。
          </small>
        </p>
      )}
    </div>
  )
}

function NextPaymentDate(props: {
  nextPaymentDate: string | null | undefined
  nextPaymentAmount: number | null | undefined
  status: SubscriptionStatus | null | undefined
}) {
  const { nextPaymentDate, nextPaymentAmount, status } = props

  let paymentDate: string
  switch (status) {
    case SubscriptionStatus.Canceling:
    case SubscriptionStatus.Canceled:
      paymentDate = 'なし'
      break
    default:
      paymentDate = nextPaymentDate ? formatDateJP(nextPaymentDate) : 'なし'
  }

  return (
    <div className="text-secondary-text">
      <div className="flex items-center space-x-4">
        <p className="text-[13px] font-bold md:text-[14px] lg:text-[15px]">
          {paymentDate}
        </p>
        {paymentDate !== 'なし' && nextPaymentAmount && (
          <p className="text-[13px] font-bold md:text-[14px] lg:text-[15px]">
            {formatNumberWithCommas(nextPaymentAmount)} 円(税込)
          </p>
        )}
      </div>

      {nextPaymentDate && status == SubscriptionStatus.Trialing && (
        <small className="text-[13px] text-caution">
          ※ この日から課金が発生します
        </small>
      )}
    </div>
  )
}

interface SiblingDiscountInfoProps {
  paymentType?: UserPaymentType | null | undefined
  playersCount: number
  defaultPaymentMethod?: Maybe<PaymentMethod>
  hasSubscription: Maybe<boolean>
  hasCurriculumLicense: Maybe<boolean>
}

function SiblingDiscountInfo(props: SiblingDiscountInfoProps) {
  const { paymentType, playersCount, defaultPaymentMethod } = props
  const [modalOpen, setModalOpen] = useState(false)
  const router = useRouter()
  const onlySubscription = props.hasSubscription && !props.hasCurriculumLicense
  const onlyCurriculumLicense =
    !props.hasSubscription && props.hasCurriculumLicense

  return (
    <section>
      <div className="flex flex-col rounded-lg bg-white md:flex-row md:rounded-[10px]">
        <div className="flex h-[55px] flex-shrink-0 items-center justify-center rounded-tl-lg rounded-tr-lg bg-primary-20 md:h-auto md:w-[199px] md:rounded-bl-[10px] md:rounded-tl-[10px] md:rounded-tr-none">
          <Image
            src="/images/sibling-discount@2x.png"
            width={133}
            height={45}
            alt="きょうだいでお得に受講"
            className="h-[45px] object-contain md:h-[55px] md:w-[162px]"
          />
        </div>
        <div className="flex w-full flex-col items-center p-[12px_40px_22px] md:flex-row md:items-start md:p-[20px_20px_20px_28px] lg:p-[20px_30px_20px_28px]">
          <div className="text-center tracking-wider md:text-left">
            <span className="text-[13px] md:text-[15px] lg:text-[16px]">
              追加されたごきょうだいは
            </span>
            <span className="block text-[18px] font-bold text-caution md:text-[21px] lg:ml-[3px] lg:inline">
              料金が30%オフ！
            </span>
            <p className="mt-[6px] text-[14px] text-secondary-text lg:text-[15px]">
              年払いプラン: 月あたり 2,436円 (税込)
            </p>
            <p className="text-[14px] text-secondary-text lg:text-[15px]">
              月払いプラン: 月々 2,786円 (税込)
            </p>
          </div>

          {paymentType == UserPaymentType.AppleInApp ||
            paymentType == UserPaymentType.GoogleInApp ? (
            <>
              <Button
                variant="outline"
                size="sm"
                className="mt-3 h-[44px] w-[176px] rounded-xl pl-[6px] text-[15px] leading-[normal] !shadow-none disabled:border-none md:ml-auto md:mt-5"
                onClick={() => setModalOpen(true)}
                disabled={!defaultPaymentMethod || playersCount > 4}
              >
                <IconPlus width={30} height={30} />
                {onlySubscription || onlyCurriculumLicense
                  ? '申し込む'
                  : 'プレイヤーを追加'}
              </Button>
              <AddPlayerOnMobileModal
                isOpen={modalOpen}
                onRequestClose={() => setModalOpen(false)}
              />
            </>
          ) : (
            <Button
              variant="outline"
              size="sm"
              className="mt-3 h-[44px] w-[176px] rounded-xl pl-[6px] text-[15px] leading-[normal] !shadow-none disabled:border-none md:ml-auto md:mt-5"
              disabled={!defaultPaymentMethod || playersCount > 4}
              onClick={() => router.push('/web/subscription')}
            >
              <IconPlus width={30} height={30} />
              {onlySubscription || onlyCurriculumLicense
                ? '申し込む'
                : 'プレイヤーを追加'}
            </Button>
          )}
        </div>
      </div>
      <p className="mt-3 text-xs leading-[150%] tracking-wider text-secondary-text">
        ※
        きょうだい割は2人目から適用されます。主契約の自動更新が停止済み、停止申請中の場合は適用されませんのでご注意ください。
      </p>
    </section>
  )
}

export const PlanInfo_viewer = graphql(
  `
    fragment PlanInfo_viewer on User {
      paymentType
      players {
        id
        subscriptionInfo {
          id
          status
          isSiblingDiscounted
        }
        ...PlayerSubscriptionInfo_player
      }
      defaultPaymentMethod {
        displayName
      }
      hasCurriculumLicense
      hasSubscription
      mainSubscriptionIsTrialing
    }
  `,
  [PlayerSubscriptionInfo_player]
)
